import { Truck as TruckIcon, ChevronDown, ArrowDownWideNarrow, ArrowDownAZ, Check } from 'lucide-react';
import { useFilters } from '@/contexts/FilterContext';
import { useUnitStatusOptional } from '@/contexts/UnitStatusContext';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useUserRole } from '@/hooks/useUserRole';
import { useUserCompany } from '@/hooks/useUserCompany';
import type { Unit, UnitFilterData, IndustryType } from '@/types';
import { useState, useMemo, forwardRef } from 'react';
import { MODEL_LABELS, INDUSTRY_TYPE_LABELS, INDUSTRY_TYPE_ORDER, MODEL_TYPE_MAPPING } from '@/lib/constants';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

interface UnitModelFilterProps {
  units?: (Unit | UnitFilterData)[];
  /** Override: selected model codes (for sidebar mode with separate state) */
  selectedModelCodes?: string[];
  /** Override: callback when models change (for sidebar mode) */
  onModelsChange?: (modelCodes: string[]) => void;
  /** Override: selected industry types (for sidebar mode) */
  selectedIndustryTypes?: IndustryType[];
  /** Override: selected group IDs for filtering (for sidebar mode) */
  selectedGroupIds?: string[];
  /** Unit counts per model code for the selected groups (for sidebar mode) */
  modelCounts?: Record<string, number>;
  /** When true, this is sidebar mode */
  sidebarMode?: boolean;
  /** When true, applies highlight ring/glow effect to draw attention to the filter */
  highlight?: boolean;
}

export const UnitModelFilter = forwardRef<HTMLButtonElement, UnitModelFilterProps>(function UnitModelFilter({
  units = [],
  selectedModelCodes: propsSelectedModelCodes,
  onModelsChange,
  selectedIndustryTypes: propsSelectedIndustryTypes,
  selectedGroupIds: propsSelectedGroupIds,
  modelCounts,
  sidebarMode = false,
  highlight = false,
}, ref) {
  const { selectedModels: contextSelectedModels, setSelectedModels, selectedIndustryTypes: contextSelectedIndustryTypes, selectedCompanyIds, selectedGroupIds: contextSelectedGroupIds, selectedStatuses, units: contextUnits } = useFilters();
  const unitStatusContext = useUnitStatusOptional();
  const isMobile = useIsMobile();
  const { isGroupAdmin, isSuperadmin } = useUserRole();
  const { company } = useUserCompany();
  const [sortBy, setSortBy] = useState<'name' | 'count'>('name');
  const [menuOpen, setMenuOpen] = useState(false);

  // Use props if provided (sidebar mode), otherwise use context (status bar mode)
  const selectedModels = propsSelectedModelCodes ?? contextSelectedModels;
  const selectedIndustryTypes = propsSelectedIndustryTypes ?? contextSelectedIndustryTypes;
  const selectedGroupIds = propsSelectedGroupIds ?? contextSelectedGroupIds;

  // Helper to get live status for a unit (uses UnitStatusContext if available)
  const getLiveStatus = (unitId: string, fallbackStatus: string) => {
    return unitStatusContext?.getStatus(unitId) ?? fallbackStatus;
  };

  // Determine allowed industry types based on user's company industry
  // For industry-restricted companies (e.g., NBC Universal → FILM),
  // MODELS filter only shows models from that industry
  //
  // NOTE: We DON'T return [] while loading to prevent UI flicker when tab regains focus.
  // Supabase's auth client fires state changes on visibility change, which can cause
  // brief loading states. Instead, we use selectedIndustryTypes as fallback while loading.
  //
  // In sidebar mode, we always use the provided selectedIndustryTypes (from props)
  const allowedTypes = useMemo((): IndustryType[] => {
    // In sidebar mode, use provided industry types directly
    if (sidebarMode) {
      return selectedIndustryTypes;
    }

    // Superadmins and internal Hollywood Trucks users see all selected industry types
    if (isSuperadmin || company?.is_internal) {
      return selectedIndustryTypes;
    }

    // If company has a specific industry type, restrict to that industry only
    if (company?.industry) {
      // Only show models from the company's industry
      return [company.industry];
    }

    // Default: use selected industry types (for companies without industry restriction)
    // This also serves as fallback while loading to prevent flicker
    return selectedIndustryTypes;
  }, [selectedIndustryTypes, isSuperadmin, company, sidebarMode]);

  const allModels = Object.keys(MODEL_LABELS);
  const isAllSelected = selectedModels.length === allModels.length;

  // Filter units based on OTHER filters (not model) for accurate counts
  // This shows meaningful counts like "2RE (3)" when only 3 2RE units match other filters
  // Use prop units if provided, otherwise fall back to context units
  const unitsSource = units.length > 0 ? units : contextUnits;
  const filteredUnits = useMemo(() => {
    if (allowedTypes.length === 0) return []; // If no types selected, show nothing

    // If units are provided via props (sidebar/modal mode), use them directly
    // They're already filtered by parent (Company) - just apply remaining filters
    if (units.length > 0) {
      return units.filter((unit: Unit | UnitFilterData) => {
        // Industry type filter
        if (!allowedTypes.includes(unit.industry_type || 'film')) {
          return false;
        }
        // Group filter (if provided)
        if (selectedGroupIds.length > 0) {
          // Handle both Unit type (nested primary_group.id) and UnitFilterData type (flat primary_group_id)
          const groupId = unit.primary_group_id || ('primary_group' in unit && unit.primary_group?.id);
          // Handle both flat company_id and nested company.id for unassigned matching
          const companyId = unit.company_id || ('company' in unit && unit.company?.id);
          const matchesGroup = (groupId && selectedGroupIds.includes(groupId)) ||
                              (companyId && selectedGroupIds.includes(`__unassigned_${companyId}__`) && !groupId);
          if (!matchesGroup) return false;
        }
        // Note: We do NOT filter by model here - that's what this dropdown controls
        return true;
      });
    }

    // Context mode: apply all filters from context
    return unitsSource.filter((unit: Unit | UnitFilterData) => {
      // Industry type filter
      if (!allowedTypes.includes(unit.industry_type || 'film')) {
        return false;
      }
      // Company filter
      if (selectedCompanyIds.length > 0 && unit.company_id && !selectedCompanyIds.includes(unit.company_id)) {
        return false;
      }
      // Group filter
      if (selectedGroupIds.length > 0) {
        // Handle both Unit type (nested primary_group.id) and UnitFilterData type (flat primary_group_id)
        const groupId = unit.primary_group_id || ('primary_group' in unit && unit.primary_group?.id);
        // Handle both flat company_id and nested company.id for unassigned matching
        const companyId = unit.company_id || ('company' in unit && unit.company?.id);
        const matchesGroup = (groupId && selectedGroupIds.includes(groupId)) ||
                            (companyId && selectedGroupIds.includes(`__unassigned_${companyId}__`) && !groupId);
        if (!matchesGroup) return false;
      }
      // Status filter - use live status from UnitStatusContext
      const liveStatus = getLiveStatus(unit.id, unit.status);
      if (selectedStatuses.length > 0 && selectedStatuses.length < 5 && !selectedStatuses.includes(liveStatus)) {
        return false;
      }
      // Note: We do NOT filter by model here - that's what this dropdown controls
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getLiveStatus is stable via unitStatusContext
  }, [units, unitsSource, allowedTypes, selectedCompanyIds, selectedGroupIds, selectedStatuses, unitStatusContext]);

  // Calculate counts based on filtered units per industry
  // Structure: Industry -> Model -> Count
  // In sidebar mode with modelCounts prop, use those counts distributed across selected industries
  const countsByIndustryAndModel = useMemo(() => {
    // If modelCounts prop provided (sidebar mode), use it
    if (modelCounts && sidebarMode) {
      const counts: Record<string, Record<string, number>> = {};

      // Distribute counts across selected industries based on model type mapping
      Object.entries(modelCounts).forEach(([modelCode, count]) => {
        const types = MODEL_TYPE_MAPPING[modelCode] || ['film'];

        types.forEach(type => {
          if (allowedTypes.includes(type)) {
            if (!counts[type]) counts[type] = {};
            // For sidebar, just use the count directly (model appears in one industry)
            counts[type][modelCode] = count;
          }
        });
      });

      return counts;
    }

    // Normal mode: compute from filtered units
    const counts: Record<string, Record<string, number>> = {};

    filteredUnits.forEach(t => {
       const type = t.industry_type || 'film';
       const model = t.unit_code.substring(0, 3);

       if (!counts[type]) counts[type] = {};
       counts[type][model] = (counts[type][model] || 0) + 1;
    });

    return counts;
  }, [filteredUnits, modelCounts, sidebarMode, allowedTypes]);

  // Helper to set selected models - uses prop callback in sidebar mode, context in status bar mode
  const updateSelectedModels = (models: string[]) => {
    if (onModelsChange) {
      onModelsChange(models);
    } else {
      setSelectedModels(models);
    }
  };

  const handleToggle = (model: string) => {
    if (isAllSelected) {
      // If "All" was active, switch to single selection
      updateSelectedModels([model]);
    } else if (selectedModels.includes(model)) {
      const newSelection = selectedModels.filter(m => m !== model);
      // If deselecting results in empty, we allow it (means show nothing)
      updateSelectedModels(newSelection);
    } else {
      // Add to selection
      const newSelection = [...selectedModels, model];
      // If now all are selected
      if (newSelection.length === allModels.length) {
        updateSelectedModels(allModels);
      } else {
        updateSelectedModels(newSelection);
      }
    }
  };

  const handleSelectAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only select models that are visible (from allowed industries)
    const visibleModels = Object.values(modelsByType).flat();
    // Remove duplicates (models can appear in multiple industries)
    const uniqueVisibleModels = [...new Set(visibleModels)];
    updateSelectedModels(uniqueVisibleModels.length > 0 ? uniqueVisibleModels : allModels);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // User request: "pressing clear, unchecks them all"
    updateSelectedModels([]);
    // Close menu immediately after clearing
    setMenuOpen(false);
  };

  // Group models by type
  const modelsByType = useMemo(() => {
    const grouped: Record<string, string[]> = {};
    
    allModels.forEach(modelCode => {
      const types = MODEL_TYPE_MAPPING[modelCode] || ['film']; // Default to film (as array)
      
      // Check each type this model belongs to
      types.forEach(type => {
        // Only include if the type is in allowedTypes (industry-restricted for group_admins)
        if (allowedTypes.includes(type)) {
           if (!grouped[type]) grouped[type] = [];
           // Avoid duplicates if model is already in the list (shouldn't happen with this logic but safe)
           if (!grouped[type].includes(modelCode)) {
             grouped[type].push(modelCode);
           }
        }
      });
    });

    return grouped;
  }, [allModels, allowedTypes]);

  // Calculate effective selected models count
  // This is the intersection of selected models and available models (filteredUnits)
  // But filteredUnits contains TRUCKS, not models.
  // availableModels would be the set of model codes present in filteredUnits.
  const effectiveSelectedCount = useMemo(() => {
    if (allowedTypes.length === 0) return 0;
    
    // Get set of available models from filtered units
    const availableModels = new Set(filteredUnits.map(t => t.unit_code.substring(0, 3)));
    
    // Count selected models that are also available
    return selectedModels.filter(m => availableModels.has(m)).length;
  }, [selectedModels, filteredUnits, allowedTypes]);

  // Trigger button shared between desktop dropdown and mobile sheet
  const triggerButton = (
    <button
      ref={ref}
      className={cn(
        "flex items-center gap-1.5 rounded-md bg-muted/40 hover:bg-accent transition-all duration-200 border border-border/50 hover:border-border text-muted-foreground hover:text-foreground",
        sidebarMode
          ? "w-full h-8 px-3"
          : isMobile
            ? "h-9 px-1.5"
            : "px-3 xl:px-4 py-2",
        highlight && "ring-2 ring-[#8FB560] ring-offset-2 ring-offset-background"
      )}
    >
      <TruckIcon className="h-4 w-4" />
      {sidebarMode ? (
        <span className="tracking-wide text-xs uppercase flex-1 text-left truncate">MODEL</span>
      ) : !isMobile ? (
        <span className={`font-medium tracking-wide text-xs ${isGroupAdmin ? '' : 'hidden xl:inline'}`}>MODEL</span>
      ) : null}
      {effectiveSelectedCount > 0 && (
         <span className="text-[10px] leading-none bg-[#8FB560] text-black rounded-full px-1.5 py-0.5 min-w-[16px] text-center font-semibold">
           {effectiveSelectedCount}
         </span>
      )}
      <ChevronDown className="h-3.5 w-3.5" />
    </button>
  );

  // Filter list content
  const filterContent = allowedTypes.length === 0 ? (
    <div className="px-2 py-8 text-center text-sm text-muted-foreground">
      Select an industry first
    </div>
  ) : (
    <>
      <div className={cn(
        "flex items-center justify-between sticky top-0 bg-popover z-10 border-b border-border flex-shrink-0",
        isMobile && !sidebarMode ? "px-4 py-3" : "px-2 py-1.5"
      )}>
        <button
          className="text-[10px] text-muted-foreground uppercase cursor-pointer hover:text-foreground flex items-center gap-1 group font-semibold"
          onClick={() => setSortBy(prev => prev === 'name' ? 'count' : 'name')}
        >
          MODELS
          {sortBy === 'count' ? (
            <ArrowDownWideNarrow className="w-3 h-3 text-primary" />
          ) : (
            <ArrowDownAZ className="w-3 h-3 opacity-0 group-hover:opacity-50" />
          )}
        </button>
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleSelectAll}
            className="text-xs text-muted-foreground hover:text-primary hover:underline"
          >
            All
          </button>
          <span className="text-xs text-[#8FB560]">|</span>
          <button
            onClick={handleClear}
            className="text-xs text-primary hover:underline"
          >
            Clear
          </button>
        </div>
      </div>

      <div className={cn(isMobile && !sidebarMode ? "px-4 py-2" : "")}>
        {INDUSTRY_TYPE_ORDER.map((type, index) => {
          const models = modelsByType[type];
          if (!models || models.length === 0) return null;

          // Sort models
          const sortedModels = [...models].sort((a, b) => {
             const countA = (countsByIndustryAndModel[type] || {})[a] || 0;
             const countB = (countsByIndustryAndModel[type] || {})[b] || 0;

             if (sortBy === 'count') {
               return countB - countA;
             }
             return MODEL_LABELS[a].localeCompare(MODEL_LABELS[b]);
          });

          // Calculate type total
          const typeTotal = sortedModels.reduce((sum, m) => sum + ((countsByIndustryAndModel[type] || {})[m] || 0), 0);

          return (
            <div key={type}>
              {/* Industry section header */}
              <div className={cn(
                "text-[10px] text-muted-foreground uppercase tracking-wider bg-muted py-1.5 px-2 flex justify-between items-center",
                index > 0 && "mt-3 border-t border-border",
                isMobile && !sidebarMode && "px-0 mx-0"
              )}>
                <span>{INDUSTRY_TYPE_LABELS[type]}</span>
                <span className="text-xs">({typeTotal})</span>
              </div>

              {sortedModels.map(modelCode => {
                const count = (countsByIndustryAndModel[type] || {})[modelCode] || 0;
                const isSelected = selectedModels.includes(modelCode);

                return isMobile && !sidebarMode ? (
                  <button
                    key={modelCode}
                    onClick={() => handleToggle(modelCode)}
                    className="flex items-center justify-between w-full py-3 border-b border-border/50 last:border-b-0"
                  >
                    <span className="flex items-center gap-3">
                      <span className={cn(
                        "flex h-5 w-5 items-center justify-center rounded border",
                        isSelected ? "bg-[#8FB560] border-[#8FB560]" : "border-muted-foreground/50"
                      )}>
                        {isSelected && <Check className="h-3.5 w-3.5 text-black" />}
                      </span>
                      <span className="text-sm">{MODEL_LABELS[modelCode]} ({modelCode})</span>
                    </span>
                    <span className="text-xs text-muted-foreground">({count})</span>
                  </button>
                ) : (
                  <DropdownMenuCheckboxItem
                    key={modelCode}
                    checked={isSelected}
                    onCheckedChange={() => handleToggle(modelCode)}
                    onSelect={(e) => e.preventDefault()}
                    className="flex items-center justify-between"
                  >
                    <span>{MODEL_LABELS[modelCode]} ({modelCode})</span>
                    <span className="text-xs text-muted-foreground ml-2">({count})</span>
                  </DropdownMenuCheckboxItem>
                );
              })}
            </div>
          );
        })}
      </div>
    </>
  );

  // Mobile (non-sidebar): Full-screen bottom sheet
  if (isMobile && !sidebarMode) {
    return (
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetTrigger asChild>
          {triggerButton}
        </SheetTrigger>
        <SheetContent side="bottom" className="h-[100dvh] p-0 rounded-t-xl">
          <SheetHeader className="px-4 py-4 border-b border-border">
            <SheetTitle className="text-lg font-semibold">Model Filter</SheetTitle>
          </SheetHeader>
          <div className="overflow-y-auto flex-1 h-[calc(100dvh-65px)]" key={selectedModels.join(',')}>
            {filterContent}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop or sidebar mode: Regular dropdown
  return (
    <DropdownMenu modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        {triggerButton}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={cn(
          'w-64',
          sidebarMode ? 'max-h-[50vh]' : 'max-h-[80vh]',
          'overflow-y-auto'
        )}
        key={selectedModels.join(',')}
      >
        {filterContent}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
